/**
 * HOME PAGE (Home Organization) - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Inline tokens only.
 */

import React, { useState, useEffect } from 'react';
import { Screen, Section, Panel, Button, Badge, Modal, Input, Select, Textarea } from '../design/primitives';
import { SPACING, COLORS, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import {
  BuildingOfficeIcon,
  PlusIcon,
  HomeIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface HomeArea {
  id: number;
  name: string;
  area_type: string;
  description: string;
  size_sqft: number | null;
  color_code: string;
  condition: string;
  last_maintained: string | null;
  next_maintenance: string | null;
  items_count: number;
  tasks_count: number;
  projects_count: number;
  category_name: string | null;
  business_name: string | null;
  created_at: string;
  updated_at: string;
}

const AREA_TYPE_ICONS: Record<string, any> = {
  room: HomeIcon,
  outdoor: BuildingOfficeIcon,
  garage: WrenchScrewdriverIcon,
  other: BuildingOfficeIcon,
};

const CONDITION_STYLES: Record<string, { bg: string; text: string }> = {
  excellent: { bg: 'rgba(16, 185, 129, 0.15)', text: COLORS.status.success },
  good: { bg: 'rgba(59, 130, 246, 0.15)', text: COLORS.accent.default },
  fair: { bg: 'rgba(245, 158, 11, 0.15)', text: COLORS.status.warning },
  needs_attention: { bg: 'rgba(239, 68, 68, 0.15)', text: COLORS.status.error },
  remodeling: { bg: 'rgba(99, 102, 241, 0.15)', text: COLORS.status.info },
};

const HomePage: React.FC = () => {
  const [areas, setAreas] = useState<HomeArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAreaType, setSelectedAreaType] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    area_type: 'room',
    description: '',
    size_sqft: '',
    color_code: '#3B82F6',
    condition: 'good',
  });

  useEffect(() => {
    const fetchAreas = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = selectedAreaType !== 'all' ? { area_type: selectedAreaType } : {};
        const response = await apiClient.get('/api/home-areas/', { params });
        setAreas(response.data);
      } catch (err) {
        console.error('Error fetching home areas:', err);
        setError('Failed to load home areas');
      } finally {
        setLoading(false);
      }
    };

    fetchAreas();
  }, [selectedAreaType]);

  const areaTypeGroups = areas.reduce((acc, area) => {
    if (!acc[area.area_type]) {
      acc[area.area_type] = [];
    }
    acc[area.area_type].push(area);
    return acc;
  }, {} as Record<string, HomeArea[]>);

  const handleCreateArea = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        size_sqft: formData.size_sqft ? parseFloat(formData.size_sqft) : null,
      };
      await apiClient.post('/api/home-areas/', payload);
      setShowCreateModal(false);
      setFormData({
        name: '',
        area_type: 'room',
        description: '',
        size_sqft: '',
        color_code: '#3B82F6',
        condition: 'good',
      });
      const params = selectedAreaType !== 'all' ? { area_type: selectedAreaType } : {};
      const response = await apiClient.get('/api/home-areas/', { params });
      setAreas(response.data);
    } catch (err) {
      console.error('Error creating home area:', err);
      alert('Failed to create home area');
    }
  };

  const areaTypes = ['all', 'room', 'outdoor', 'garage', 'other'];

  return (
    <Screen>
      <Section
        title="Home Organization"
        action={
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}
          >
            Add Area
          </Button>
        }
      >
        {/* Filter tabs */}
        <div style={{
          display: 'flex',
          gap: SPACING[4],
          borderBottom: `1px solid ${COLORS.border.default}`,
          marginBottom: SPACING[16],
        }}>
          {areaTypes.map((type) => {
            const isActive = selectedAreaType === type;
            return (
              <button
                key={type}
                onClick={() => setSelectedAreaType(type)}
                style={{
                  padding: `${SPACING[8]} ${SPACING[12]}`,
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  fontWeight: isActive ? '600' : '500',
                  color: isActive ? COLORS.accent.default : COLORS.text.secondary,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? `2px solid ${COLORS.accent.default}` : '2px solid transparent',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                  marginBottom: '-1px',
                }}
              >
                {type}
              </button>
            );
          })}
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: SPACING[48], color: COLORS.text.tertiary }}>
            Loading...
          </div>
        )}

        {/* Error State */}
        {error && (
          <Panel style={{
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${COLORS.status.error}`,
            color: COLORS.status.error,
            marginBottom: SPACING[16],
          }}>
            {error}
          </Panel>
        )}

        {/* Empty State */}
        {!loading && !error && areas.length === 0 && (
          <Panel>
            <div style={{ textAlign: 'center', padding: SPACING[48] }}>
              <BuildingOfficeIcon style={{ width: '48px', height: '48px', color: COLORS.text.tertiary, margin: '0 auto', marginBottom: SPACING[16] }} />
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
              }}>
                No areas
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.tertiary,
              }}>
                Get started by creating your first home area.
              </p>
            </div>
          </Panel>
        )}

        {/* Areas Grid */}
        {!loading && !error && selectedAreaType === 'all' ? (
          Object.entries(areaTypeGroups).map(([type, typeAreas]) => (
            <div key={type} style={{ marginBottom: SPACING[24] }}>
              <h2 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[16],
                textTransform: 'capitalize',
              }}>
                {type}s
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: SPACING[16],
              }}>
                {typeAreas.map((area) => (
                  <AreaCard key={area.id} area={area} />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: SPACING[16],
          }}>
            {areas.map((area) => (
              <AreaCard key={area.id} area={area} />
            ))}
          </div>
        )}
      </Section>

      {/* Create Area Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Area"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateArea}>
              Create Area
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateArea} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          <div>
            <label style={{
              display: 'block',
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '600',
              color: COLORS.text.secondary,
              marginBottom: SPACING[4],
            }}>
              Name *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Living Room, Garage, Pool"
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '600',
              color: COLORS.text.secondary,
              marginBottom: SPACING[4],
            }}>
              Area Type *
            </label>
            <Select
              value={formData.area_type}
              onChange={(e) => setFormData({ ...formData, area_type: e.target.value })}
              options={[
                { value: 'room', label: 'Room' },
                { value: 'outdoor', label: 'Outdoor' },
                { value: 'garage', label: 'Garage' },
                { value: 'other', label: 'Other' },
              ]}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '600',
              color: COLORS.text.secondary,
              marginBottom: SPACING[4],
            }}>
              Description
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe this area..."
              rows={3}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '600',
              color: COLORS.text.secondary,
              marginBottom: SPACING[4],
            }}>
              Size (sq ft)
            </label>
            <Input
              type="number"
              value={formData.size_sqft}
              onChange={(e) => setFormData({ ...formData, size_sqft: e.target.value })}
              placeholder="e.g., 250"
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '600',
              color: COLORS.text.secondary,
              marginBottom: SPACING[4],
            }}>
              Color
            </label>
            <input
              type="color"
              value={formData.color_code}
              onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
              style={{
                width: '100%',
                height: '40px',
                padding: SPACING[4],
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                cursor: 'pointer',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '600',
              color: COLORS.text.secondary,
              marginBottom: SPACING[4],
            }}>
              Condition *
            </label>
            <Select
              value={formData.condition}
              onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
              options={[
                { value: 'excellent', label: 'Excellent' },
                { value: 'good', label: 'Good' },
                { value: 'fair', label: 'Fair' },
                { value: 'needs_attention', label: 'Needs Attention' },
                { value: 'remodeling', label: 'Remodeling' },
              ]}
            />
          </div>
        </form>
      </Modal>
    </Screen>
  );
};

const AreaCard: React.FC<{ area: HomeArea }> = ({ area }) => {
  const Icon = AREA_TYPE_ICONS[area.area_type] || BuildingOfficeIcon;
  const conditionStyle = CONDITION_STYLES[area.condition] || { bg: COLORS.bg.hover, text: COLORS.text.secondary };

  return (
    <Panel
      style={{
        borderLeft: `4px solid ${area.color_code}`,
        cursor: 'pointer',
        transition: 'all 0.1s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              padding: SPACING[8],
              borderRadius: RADIUS,
              backgroundColor: `${area.color_code}20`,
            }}
          >
            <Icon style={{ width: '24px', height: '24px', color: area.color_code }} />
          </div>
          <div style={{ marginLeft: SPACING[12] }}>
            <h3 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
            }}>
              {area.name}
            </h3>
            {area.size_sqft && (
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                color: COLORS.text.tertiary,
                margin: 0,
              }}>
                {area.size_sqft} sq ft
              </p>
            )}
          </div>
        </div>
        <span style={{
          display: 'inline-block',
          padding: `${SPACING[4]} ${SPACING[8]}`,
          borderRadius: RADIUS,
          backgroundColor: conditionStyle.bg,
          color: conditionStyle.text,
          fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.tiny,
          fontWeight: '500',
          textTransform: 'capitalize',
        }}>
          {area.condition.replace('_', ' ')}
        </span>
      </div>

      {area.description && (
        <p style={{
          fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.default,
          color: COLORS.text.secondary,
          marginBottom: SPACING[12],
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}>
          {area.description}
        </p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: SPACING[16],
        paddingTop: SPACING[12],
        borderTop: `1px solid ${COLORS.border.default}`,
      }}>
        <div>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.tiny,
            color: COLORS.text.tertiary,
            margin: 0,
          }}>
            Items
          </p>
          <p style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}>
            {area.items_count}
          </p>
        </div>
        <div>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.tiny,
            color: COLORS.text.tertiary,
            margin: 0,
          }}>
            Tasks
          </p>
          <p style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}>
            {area.tasks_count}
          </p>
        </div>
        <div>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.tiny,
            color: COLORS.text.tertiary,
            margin: 0,
          }}>
            Projects
          </p>
          <p style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}>
            {area.projects_count}
          </p>
        </div>
      </div>

      {area.next_maintenance && (
        <div style={{
          marginTop: SPACING[12],
          paddingTop: SPACING[12],
          borderTop: `1px solid ${COLORS.border.default}`,
        }}>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.tiny,
            color: COLORS.text.tertiary,
            margin: 0,
          }}>
            Next maintenance: {new Date(area.next_maintenance).toLocaleDateString()}
          </p>
        </div>
      )}
    </Panel>
  );
};

export default HomePage;
